import { APOLLO_OPTIONS, Apollo, NamedOptions, APOLLO_NAMED_OPTIONS } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http'
import {
    ApolloClient,
    ApolloLink,
    DocumentNode,
    split,
    InMemoryCache,
} from '@apollo/client/core'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { print } from 'graphql/language/printer'
import { TranslateService } from '@ngx-translate/core'
import { any } from 'ramda'

import { Dialog } from '@angular/cdk/dialog'
import { isPlatformBrowser } from '@angular/common'
import {
    Injector,
    NgModule,
    PLATFORM_ID,
    makeStateKey,
    TransferState,
} from '@angular/core'

import { DialogComponent, DialogData } from '@app-domains/ui/components/dialog/dialog.component'
import { environment } from '@app-environments/environment'
import { devConsole } from '@app-lib/common.lib'
import { APOLLO_CACHE } from '@app-modules/graphql/graphql.tokens'
import { AuthStateStore, CookieAuthStorageService } from '@app-services'
import {
    createApiAuthLink,
    createApiHttpLink,
    createSubscriptionsHttpLink,
    createValidationError,
    cacheConfig,
    ValidationError,
} from '@app-modules/graphql/graphql.module.lib'

const STATE_KEY = makeStateKey<any>('apollo.state')

function createApolloSubscription(
    httpLink: HttpLink,
    injector: Injector,
): NamedOptions {
    const platformId = injector.get(PLATFORM_ID)
    const isBrowser = isPlatformBrowser(platformId)

    const apiLink = createSubscriptionsHttpLink(httpLink, environment)

    if (isBrowser) {
        const wsLink = new GraphQLWsLink(createClient({
            url: `${environment.subscriptionGraphql.schema}://${environment.subscriptionGraphql.hostname}/graphql`,
        }))

        const splitLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query)
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                )
            },
            wsLink,
            apiLink,
        )

        return {
            subscription: {
                cache: new InMemoryCache(),
                link: splitLink,
            },
        }
    }

    return {
        subscription: {
            cache: new InMemoryCache(),
            link: apiLink,
        },
    }
}

function createApollo(
    authStorageService: AuthStateStore,
    injector: Injector,
    transferState: TransferState,
    dialog: Dialog,
    translate: TranslateService,
    cache: InMemoryCache,
    httpLink: HttpLink,
): ApolloClient.Options {
    const platformId = injector.get(PLATFORM_ID)
    const isBrowser = isPlatformBrowser(platformId)

    const errorLink = new ErrorLink(({ error, result, operation }) => {
        devConsole.groupCollapsed(`%c${operation.operationName}`, 'background-color: red; color: white;')
        if (operation) {
            devConsole.error(print(operation.query as DocumentNode))
            devConsole.error(operation.variables)
        }

        if (CombinedGraphQLErrors.is(error)) {
            const graphQLErrors = error.errors
            graphQLErrors.forEach(({ message, locations, path }) => {
                devConsole.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
            })

            if (result) {
                if (! result.extensions) {
                    result.extensions = {}
                }

                if (any(e => e.message === 'Unauthenticated.', graphQLErrors)) {
                    authStorageService.clearAuthState()
                }

                result.extensions.validationErrors = graphQLErrors.reduce((acc, err) => {
                    if (err.extensions?.category === 'validation') {
                        acc.push(...createValidationError(
                            err.extensions.validation as Record<string, string[]>,
                        ))
                        devConsole.log('error', err.extensions.validation)
                    }
                    return acc
                }, [] as Array<ValidationError>)

                const hasServerErrors = graphQLErrors.some(
                    err => err.extensions?.category === 'internal',
                )

                const currentDialog = dialog.getDialogById('server-error')

                if (! currentDialog && hasServerErrors && isBrowser) {
                    dialog.open(DialogComponent, {
                        id: 'server-error',
                        data: {
                            title: translate.instant('alerts.no-network-title'),
                            message: translate.instant('alerts.no-network-message'),
                        } as DialogData,
                    })
                }
            }
        } else if (isBrowser) {
            devConsole.error(`[Network error]: ${error}`)
        }

        devConsole.groupEnd()
    })

    if (isBrowser) {
        const state = transferState.get(STATE_KEY, null)

        if (state) {
            console.groupCollapsed('Apollo Client: Restoring state')
            console.debug(state)
            console.groupEnd()

            cache.restore(state)
        }
    } else {
        transferState.onSerialize(STATE_KEY, () => {
            return cache.extract()
        })
        transferState.set(STATE_KEY, cache.extract())
    }

    const link = ApolloLink.from([
        errorLink,
        createApiAuthLink(authStorageService),
        createApiHttpLink(httpLink, environment),
    ])

    const fetchPolicy = isBrowser
        ? 'cache-first'
        : 'cache-only'

    const defaultOptions = {
        mutate: { errorPolicy: 'all' as const },
        watchQuery: { errorPolicy: 'all' as const },
        query: {
            fetchPolicy,
            errorPolicy: 'all' as const,
        },
    }

    return {
        cache,
        devtools: { enabled: true },
        link,
        // Must be server-only: when true, Apollo prioritizes cache and downgrades
        // network-only / cache-and-network, so the browser never hits the API after SSR
        // — fine locally if cache is warm, broken in prod.
        ssrMode: ! isBrowser,
        // Cast: AC4 ties QueryResult.data optionality to DeclareDefaultOptions; keep
        // runtime errorPolicy 'all' without forcing DeepPartial/undefined data everywhere.
        defaultOptions: defaultOptions as ApolloClient.DefaultOptions,
    }
}

@NgModule({
    providers: [
        Apollo,
        {
            provide: APOLLO_CACHE,
            useValue: new InMemoryCache(cacheConfig),
        },
        {
            provide: APOLLO_NAMED_OPTIONS,
            useFactory: createApolloSubscription,
            deps: [
                HttpLink,
                Injector,
            ],
        },
        {
            provide: APOLLO_OPTIONS,
            useFactory: createApollo,
            deps: [
                CookieAuthStorageService,
                Injector,
                TransferState,
                Dialog,
                TranslateService,
                APOLLO_CACHE,
                HttpLink,
            ],
        },
    ],
})

export class GraphQLModule { }
